import { Component, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { ApiSettings } from '../../../../core/constants/constants';
import { AvatarConstants } from '../../../../core/constants/avatar.constants';
import { MembersMessagingConstants } from '../../../../core/constants/messaging.constants';
import { MemberDirectoryFilter } from '../../../../core/filters/member-directory.filter';
import { MemberDirectoryEntryModel } from '../../../../core/models/data/member-directory-entry.model';
import { MemberService } from '../../../../core/services/member.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { MemberUtilities } from '../../../../core/utilities/member.utilities';

import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';
import { AvatarComponent } from '../../../../parts/avatar/avatar.component';
import { CountryCodePipe, PhoneExtensionPipe, PhonePipe } from '../../../../core/pipes/phone-number/phone-number.pipe';
import { NoValuePipe } from '../../../../core/pipes/no-value/no-value.pipe';

@Component({
	selector: 'ms-member-directory',
	standalone: true,
	imports: [
		PageTitleComponent,
		FormsModule,
		FaDuotoneIconComponent,
		AvatarComponent,
		InViewPortDirective,
		TooltipDirective,
		NoValuePipe
	],
	templateUrl: './member-directory.page.html',
	styleUrl: './member-directory.page.scss',
	host: { 'style': 'display:contents' }
})
export class MemberDirectoryPage {

	//#region Dependencies

	private memberService = inject(MemberService);
	private userSessionService = inject(UserSessionService);

	//#endregion Dependencies

	//#region State

	// What the search box holds, versus what the loaded page was actually fetched with. Kept apart so
	// typing without submitting never makes the rendered results disagree with the empty-state copy.
	// A signal, not a plain field: the app is zoneless, so a field written outside a DOM event (here,
	// clearSearch) would not schedule the render that the ngModel binding needs.
	public searchTermInput = signal<string>('');
	public appliedSearchTerm = signal<string>('');

	public entries = signal<MemberDirectoryEntryModel[]>([]);
	// Starts true so the skeleton is on screen from the first paint: the load is kicked off by the
	// effect below, which does not run until after the first change detection.
	public isLoading = signal<boolean>(true);
	public errorMessage = signal<string | undefined>(undefined);

	// The offset the NEXT request will use, advanced from the window the server reports serving rather
	// than from the take we asked for. The two are the same whenever the server honours our take; this
	// way round simply cannot desynchronise if it ever does not.
	private skip = signal<number>(0);
	private hasNext = signal<boolean>(false);

	// The standard page size. Not sized to fill the viewport the way the table lists are: this grid
	// tops itself up while its sentinel is on screen (see the effect below), so a first page shorter
	// than the viewport pulls the next one in rather than stranding.
	public readonly take: number = ApiSettings.DEFAULT_TAKE();

	// From the shared constants, not a number picked to look right: the size becomes a `symbol-{n}px`
	// class name, and only the sizes the stylesheet defines have any dimensions at all.
	public readonly avatarSize: number = AvatarConstants.SIZE().MEMBER_CARD;
	public readonly avatarShape: string = AvatarConstants.SHAPE().MEMBER_CARD;
	public readonly avatarAdditionalAttributes: string = AvatarConstants.ADDITIONAL_ATTRIBUTES().MEMBER_CARD;

	private loadMoreSentinel = viewChild(InViewPortDirective);

	// Every load takes a ticket, and a response whose ticket is no longer current is dropped. Searching
	// and switching organization both start a fresh load, so without this a slow earlier response lands
	// on top of a newer one — showing results for a search term that is no longer in the box, or worse,
	// the previous organization's contact details under the new organization.
	private loadGeneration = 0;

	//#endregion State

	constructor() {
		// A directory belongs to one organization, so switching organizations reloads it from the top.
		// This also performs the initial load, on its first run.
		//
		// untracked matters: loadDirectory reads appliedSearchTerm and skip synchronously, and without
		// it this effect would take a dependency on both — so every sentinel-driven skip advance would
		// re-run it and reload the list from page one.
		effect(() => {
			// The id, not the organization object: any write to activeOrganization() that re-sets the
			// same organization would otherwise reload the directory from page one and discard every
			// page the sentinel has pulled in. A computed of a number only notifies on a real change.
			this.userSessionService.activeOrganizationId();
			untracked(() => this.applySearch());
		});


		// Keeps topping up while the sentinel is still on screen. IntersectionObserver only reports the
		// transition INTO view, so a page that arrives without pushing the sentinel below the fold
		// would otherwise strand the list — the observer has already fired and will not fire again
		// until something scrolls. Reading isLoading is what re-runs this the moment a load finishes.
		effect(() => {
			const sentinel = this.loadMoreSentinel();
			if (this.isLoading() || sentinel?.isInViewPort() !== true) return;

			untracked(() => this.onSentinelInView());
		});
	}

	//#region Data

	public async loadDirectory(append: boolean = false): Promise<void> {
		const generation = ++this.loadGeneration;
		const organizationId = this.userSessionService.activeOrganizationId();

		if (organizationId == null) {
			// Nothing in scope to show. The previous organization's entries have to go with it rather
			// than sit on screen attributed to no organization at all.
			this.resetResults();
			this.errorMessage.set(undefined);
			this.isLoading.set(false);
			return;
		}

		// Dropped up front on a fresh load, not swapped in when the response lands: keeping the old
		// organization's members visible while the new one loads is exactly the leak this guards.
		if (!append) this.entries.set([]);

		this.isLoading.set(true);
		this.errorMessage.set(undefined);

		const directoryFilter: MemberDirectoryFilter = {
			organizationId: organizationId,
			searchTerm: this.appliedSearchTerm() || undefined
		};

		const requestedSkip = this.skip();
		const response = await this.memberService.searchMemberDirectory(directoryFilter, requestedSkip, this.take);

		// Superseded while in flight. The load that replaced this one owns the results, the paging and
		// isLoading from here on, so this response is discarded without touching any of them.
		if (generation !== this.loadGeneration) return;

		if (response.success === true && response.data) {
			const page = response.data.data ?? [];

			// Deduplicated by memberId: a server that ignores skip would otherwise hand @for duplicate
			// track keys, which Angular treats as an error.
			this.entries.update(current => append ? MemberDirectoryPage.mergeEntries(current, page) : page);

			// Where the next window starts, taken from the paging the server reports. Deliberately not
			// derived from the number of rows received: a page is free to come back shorter than its
			// take, and counting rows would then re-request the tail of the window we just read.
			const servedSkip = response.data.skip ?? requestedSkip;
			const servedTake = response.data.take ?? this.take;
			this.skip.set(servedSkip + servedTake);

			const advanced = servedSkip + servedTake > requestedSkip;
			this.hasNext.set((response.data.hasNext ?? false) && advanced);
		} else {
			this.errorMessage.set(response.message ?? MembersMessagingConstants.MEMBERS_DIRECTORY_LOAD_FAILURE());
			if (!append) this.entries.set([]);
			this.hasNext.set(false);
		}

		this.isLoading.set(false);
	}

	private static mergeEntries(current: MemberDirectoryEntryModel[], page: MemberDirectoryEntryModel[]): MemberDirectoryEntryModel[] {
		const seen = new Set(current.map(entry => entry.memberId));
		return current.concat(page.filter(entry => !seen.has(entry.memberId)));
	}

	public applySearch(): void {
		this.appliedSearchTerm.set(this.searchTermInput().trim());
		this.resetResults();
		this.loadDirectory();
	}

	private resetResults(): void {
		this.entries.set([]);
		this.skip.set(0);
		this.hasNext.set(false);
	}

	public clearSearch(): void {
		this.searchTermInput.set('');
		this.applySearch();
	}

	/**
	 * Infinite scroll. This grid drives its own sentinel rather than using ms-pagination, whose markup
	 * is table rows — so it has to advance skip itself, which is the one thing ms-pagination would
	 * otherwise do for it.
	 *
	 * skip is advanced by loadDirectory from the window the server reports serving, so there is
	 * nothing to compute here.
	 *
	 * In-flight loads are serialised on isLoading alone; there is no debounce timer, because the
	 * top-up effect below needs to be able to fire again the instant a load finishes.
	 */
	public onSentinelInView(): void {
		if (!this.hasNext() || this.isLoading()) return;

		this.loadDirectory(true);
	}

	//#endregion Data

	//#region Display

	// Placeholder cards for the first load, so the skeleton occupies the space the real cards land in.
	// Carries dummy text because a .placeholder bar takes its width from the content it covers, and
	// negative ids cannot collide with a real member's, so @for's track never confuses the two.
	private skeletonEntries = computed<MemberDirectoryEntryModel[]>(() =>
		Array.from({ length: this.take }, (_unused, index) => <MemberDirectoryEntryModel>{
			memberId: -(index + 1),
			firstName: 'Loading',
			lastName: 'Member',
			emailAddress: 'loading@example.com',
			phoneCountryCode: '1',
			phoneNumber: '0000000000'
		})
	);

	public isInitialLoad = computed<boolean>(() => this.isLoading() && this.entries().length === 0);

	public displayEntries = computed<MemberDirectoryEntryModel[]>(() =>
		this.isInitialLoad() ? this.skeletonEntries() : this.entries()
	);

	public showEmptyState = computed<boolean>(() =>
		!this.isInitialLoad() && !this.errorMessage() && this.entries().length === 0
	);

	public showSentinel = computed<boolean>(() => this.hasNext() && !this.isInitialLoad());

	public hasAppliedSearchTerm = computed<boolean>(() => this.appliedSearchTerm().length > 0);

	public fullName(entry: MemberDirectoryEntryModel): string {
		return MemberUtilities.fullName(entry);
	}

	/**
	 * Country code, number and extension as one string. Composed here rather than in the template so the
	 * same text can be both rendered and handed to the truncation tooltip — at four cards per row a
	 * number carrying an extension is exactly the value that gets clipped.
	 *
	 * The pipes are instantiated directly because they take no dependencies; this is the same formatting
	 * the templates elsewhere apply, not a second implementation of it.
	 */
	public phoneLine(entry: MemberDirectoryEntryModel): string {
		if (!entry.phoneNumber) return '';

		return [
			MemberDirectoryPage.countryCodePipe.transform(entry.phoneCountryCode),
			MemberDirectoryPage.phonePipe.transform(entry.phoneNumber),
			MemberDirectoryPage.phoneExtensionPipe.transform(entry.phoneExtension)
		].filter(part => part.length > 0).join(' ');
	}

	private static readonly phonePipe = new PhonePipe();
	private static readonly countryCodePipe = new CountryCodePipe();
	private static readonly phoneExtensionPipe = new PhoneExtensionPipe();

	//#endregion Display
}
